import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { 
  Users, MapPin, Clock, Bell, Mail, Phone, Shield, Globe, Copy, Link2, Save, Loader2 
} from 'lucide-react';
import { toast } from 'sonner';

const GeneralTab = ({
  settings, setSettings, passwordForm, setPasswordForm,
  changingPassword, changePassword
}) => (
  <>
    <Card>
      <CardHeader>
        <CardTitle>General Settings</CardTitle>
        <CardDescription>Basic configuration for your platform</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="company_name">Company / Franchise Name</Label>
          <div className="relative">
            <Users className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              id="company_name"
              value={settings?.company_name || ''}
              onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
              placeholder="Seniors Helping Seniors® Union & Somerset County NJ"
              className="pl-10"
              data-testid="company-name-input"
            />
          </div>
          <p className="text-xs text-[#999]">This name appears on your careers page and candidate-facing communications</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="territory_slug">Territory URL Slug</Label>
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              id="territory_slug"
              value={settings?.territory_slug || ''}
              onChange={(e) => setSettings({ ...settings, territory_slug: e.target.value })}
              placeholder="union-somerset"
              className="pl-10"
              data-testid="territory-slug-input"
            />
          </div>
          {settings?.territory_slug && (
            <div className="flex items-center gap-2 mt-1 p-2.5 bg-[#4B2D6E]/5 rounded-lg border border-[#4B2D6E]/10">
              <Globe className="h-3.5 w-3.5 text-[#4B2D6E] shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-xs text-[#4B2D6E] font-medium">Your careers page:</p>
                <p className="text-xs font-mono text-[#4B2D6E]/80 truncate mt-0.5">seniorpeerconnect.com/{settings.territory_slug}/careers</p>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-[#4B2D6E] hover:bg-[#4B2D6E]/10 shrink-0"
                onClick={() => {
                  navigator.clipboard.writeText(`seniorpeerconnect.com/${settings.territory_slug}/careers`);
                  toast.success('URL copied to clipboard!');
                }}
                data-testid="copy-territory-url-btn"
              >
                <Copy className="h-3.5 w-3.5" />
              </Button>
            </div>
          )}
          <p className="text-xs text-[#999]">Creates a unique URL for your territory (e.g., "union-somerset" becomes /union-somerset/careers)</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="timezone">Time Zone</Label>
          <div className="relative">
            <Clock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 z-10 pointer-events-none" />
            <select
              id="timezone"
              value={settings?.timezone || ''}
              onChange={(e) => setSettings({ ...settings, timezone: e.target.value })}
              className="w-full pl-10 pr-4 py-2 rounded-md border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#4B2D6E]/20 focus:border-[#4B2D6E] appearance-none cursor-pointer"
              data-testid="timezone-select"
            >
              <option value="">Select your time zone...</option>
              <option value="America/New_York">Eastern Time (ET) — New York</option>
              <option value="America/Chicago">Central Time (CT) — Chicago</option>
              <option value="America/Denver">Mountain Time (MT) — Denver</option>
              <option value="America/Los_Angeles">Pacific Time (PT) — Los Angeles</option>
              <option value="America/Anchorage">Alaska Time (AKT) — Anchorage</option>
              <option value="Pacific/Honolulu">Hawaii Time (HT) — Honolulu</option>
              <option value="America/Phoenix">Arizona (no DST) — Phoenix</option>
            </select>
          </div>
          <p className="text-xs text-[#999]">Interview time slots will display in this time zone</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="notification_send_time">Daily Notification Send Time</Label>
          <div className="relative">
            <Bell className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              id="notification_send_time"
              type="time"
              value={settings?.notification_send_time || '17:00'}
              onChange={(e) => setSettings({ ...settings, notification_send_time: e.target.value })}
              className="pl-10"
              data-testid="notification-send-time"
            />
          </div>
          <p className="text-xs text-[#999]">
            Approval, hired, and declined notifications are queued and sent together at this time daily.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="interview_location">Interview Location</Label>
          <div className="relative">
            <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              id="interview_location"
              value={settings?.interview_location || ''}
              onChange={(e) => setSettings({ ...settings, interview_location: e.target.value })}
              placeholder="123 Main St, City, State"
              className="pl-10"
              data-testid="interview-location-input"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="virtual_meeting_link">Default Virtual Meeting Link</Label>
          <div className="relative">
            <Link2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              id="virtual_meeting_link"
              type="url"
              value={settings?.virtual_meeting_link || ''}
              onChange={(e) => setSettings({ ...settings, virtual_meeting_link: e.target.value })}
              placeholder="https://zoom.us/j/... or https://teams.microsoft.com/..."
              className="pl-10"
              data-testid="virtual-meeting-link-input"
            />
          </div>
          <p className="text-xs text-[#999]">This link will auto-fill when creating virtual interview slots</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex items-center justify-between p-4 bg-[#F5F5F5] rounded-lg">
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-[#4B2D6E]" />
              <div>
                <p className="font-medium">Email Notifications</p>
                <p className="text-sm text-[#666666]">Receive email alerts</p>
              </div>
            </div>
            <Switch
              checked={settings?.notification_email}
              onCheckedChange={(checked) => setSettings({ ...settings, notification_email: checked })}
              data-testid="email-notifications-switch"
            />
          </div>
          <div className="flex items-center justify-between p-4 bg-[#F5F5F5] rounded-lg">
            <div className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-[#4B2D6E]" />
              <div>
                <p className="font-medium">SMS Notifications</p>
                <p className="text-sm text-[#666666]">Receive text alerts</p>
              </div>
            </div>
            <Switch
              checked={settings?.notification_sms}
              onCheckedChange={(checked) => setSettings({ ...settings, notification_sms: checked })}
              data-testid="sms-notifications-switch"
            />
          </div>
        </div>
      </CardContent>
    </Card>

    <Card className="mt-6">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Shield className="h-5 w-5 text-[#4B2D6E]" />
          Change Password
        </CardTitle>
        <CardDescription>Update your login password</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="max-w-md space-y-3">
          <div className="space-y-2">
            <Label htmlFor="current-password">Current Password</Label>
            <Input id="current-password" type="password" value={passwordForm.current_password} onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })} placeholder="Enter current password" data-testid="current-password-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="new-password">New Password</Label>
            <Input id="new-password" type="password" value={passwordForm.new_password} onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })} placeholder="At least 8 characters" data-testid="new-password-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="confirm-password">Confirm New Password</Label>
            <Input id="confirm-password" type="password" value={passwordForm.confirm_password} onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })} placeholder="Re-enter new password" data-testid="confirm-password-input" />
          </div>
          <Button onClick={changePassword} disabled={changingPassword || !passwordForm.current_password || !passwordForm.new_password || !passwordForm.confirm_password} className="bg-[#4B2D6E] hover:bg-[#3a2357]" data-testid="change-password-btn">
            {changingPassword ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" />Changing...</>) : (<><Shield className="mr-2 h-4 w-4" />Update Password</>)}
          </Button>
        </div>
      </CardContent>
    </Card>
  </>
);

export { GeneralTab };
